// Character select's grid, as data (docs/plans/2026-10-19-character-select.md, section 6): what each face in your side's
// roster shows, where the arrow keys take the cursor, and what a tap or Enter sends. No DOM here, so tests can hold it;
// src/client/select.ts draws it.
import type { Side } from './avatars';

/** One rider's pick on your side, as `sel` sends it: [rider number, face id, locked]. */
export type SelPick = [number, number, number | boolean];
/** The server's `sel`: your side, its roster, its picks, and the seconds left. */
export interface Sel { side: Side; roster: number[]; picks: SelPick[]; left: number }

/** A face in the grid: who has it (a rider number, or null), whether that is locked, and whether it is yours. */
export interface FaceState { id: number; by: number | null; locked: boolean; mine: boolean; blocked: boolean }

/** The grid's columns: 7 (7 x 2 for 14) on a desktop, 5 on a phone (480 px wide or less). */
export const selCols = (width: number) => (width <= 480 ? 5 : 7);

/** Each roster face's state. A lock outranks a pick; your own pick outranks another's unlocked pick on the same face. */
export function faceStates(roster: number[], picks: SelPick[], me: number): FaceState[] {
  return roster.map((id) => {
    const on = picks.filter((p) => p[1] === id), lockedBy = on.find((p) => !!p[2]), mineP = on.find((p) => p[0] === me);
    const who = lockedBy ?? mineP ?? on[0];
    const by = who ? who[0] : null, locked = !!who?.[2];
    return { id, by, locked, mine: by === me, blocked: locked && by !== me };
  });
}

/** Your pick, and whether it is locked; av -1 before you pick. */
export function myPick(picks: SelPick[], me: number): { av: number; locked: boolean } {
  const p = picks.find((x) => x[0] === me);
  return p ? { av: p[1], locked: !!p[2] } : { av: -1, locked: false };
}

/** The cursor after an arrow key, in a grid of n faces and `cols` columns; the same index for any other key. */
export function moveCursor(i: number, code: string, n: number, cols: number): number {
  if (n <= 0) return 0;
  const j = code === 'ArrowLeft' ? i - 1 : code === 'ArrowRight' ? i + 1 : code === 'ArrowUp' ? i - cols : code === 'ArrowDown' ? i + cols : i;
  return j < 0 || j >= n ? i : j;
}

/**
 * What a tap on face `av` sends, or null for nothing: a face locked by someone else, or you are already locked. A tap
 * on your current pick locks it; on any other face it picks that one.
 */
export function tapMessage(states: FaceState[], av: number, mine: { av: number; locked: boolean }): { t: 'pick'; av: number; lock?: 1 } | null {
  const s = states.find((x) => x.id === av);
  if (!s || s.blocked || mine.locked) return null;
  return mine.av === av ? { t: 'pick', av, lock: 1 } : { t: 'pick', av };
}

/** The select screen's title by side. */
export const selTitle = (side: Side) => (side === 'C' ? 'You hold the line' : 'You march');
